import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import './DisplayPage.css';

const bankApps = [
  { name: '토스', logo: 'https://static.toss.im/logos/svg/logo-toss-blue.svg', scheme: 'supertoss://', androidPackage: 'viva.republica.toss', iosId: '839333328', bgColor: '#ffffff', textColor: '#555555', initial: '' },
  { name: '카카오뱅크', logo: 'https://www.kakaobank.com/static/images/web/logo_black.svg', scheme: 'kakaobank://', androidPackage: 'com.kakaobank.channel', iosId: '1258016944', bgColor: '#FEE500', textColor: '#3C1E1E', initial: '' },
  { name: 'NH농협', logo: 'https://top2020.dothome.co.kr/builder/summernote/1760429603_80d243fc05826e1c0269.png', scheme: 'newnhsmartbanking://', androidPackage: 'nh.smart.banking', iosId: '1444712671', bgColor: '#ffffff', textColor: '#555555', initial: '' },
  { name: 'KB국민', logo: '', scheme: 'kBbank://', androidPackage: 'com.kbstar.kbbank', iosId: '373742138', bgColor: '#FFD700', textColor: '#222222', initial: 'KB' },
  { name: '신한', logo: '', scheme: 'shinhanbank://', androidPackage: 'com.shinhan.sbanking', iosId: '357497364', bgColor: '#0046FF', textColor: '#ffffff', initial: '신한' },
  { name: '하나', logo: '', scheme: 'hananbank://', androidPackage: 'com.kebhana.hanapush', iosId: '386276886', bgColor: '#009490', textColor: '#ffffff', initial: '하나' },
  { name: '우리', logo: '', scheme: 'SmartBankWB://', androidPackage: 'com.wooribank.smart.npib', iosId: '379189579', bgColor: '#0073B9', textColor: '#ffffff', initial: '우리' },
  { name: '기업', logo: '', scheme: 'IBKBANK://', androidPackage: 'com.ibk.android.ionebank', iosId: '431491796', bgColor: '#005BAC', textColor: '#ffffff', initial: 'IBK' },
  { name: '케이뱅크', logo: '', scheme: 'kbank://', androidPackage: 'com.kbankwith.smartbank', iosId: '1178478159', bgColor: '#7B68EE', textColor: '#ffffff', initial: '케이뱅크' },
];

const i18n: { [key: string]: { [key: string]: string } } = {
  ko: { title: '송금 안내', copyBtn: '계좌번호 복사하기', copyDone: '✅ 복사 완료!', bankApp: '앱으로 송금하기', accountHolder: '예금주', bank: '은행', accountNumber: '계좌번호', memo: '메모', quantity: '수량', amount: '금액', totalAmount: '총 금액', expiredMsg: '이 QR코드는 만료되었습니다.', autocopied: '✅ 계좌번호가 자동으로 복사되었습니다!' },
  en: { title: 'Payment Info', copyBtn: 'Copy Account', copyDone: '✅ Copied!', bankApp: 'Open Bank App', accountHolder: 'Holder', bank: 'Bank', accountNumber: 'Account', memo: 'Memo', quantity: 'Qty', amount: 'Price', totalAmount: 'Total', expiredMsg: 'This QR code has expired.', autocopied: '✅ Account number copied automatically!' },
  zh: { title: '汇款信息', copyBtn: '复制账号', copyDone: '✅ 已复制!', bankApp: '打开银行应用', accountHolder: '户主', bank: '银行', accountNumber: '账号', memo: '备注', quantity: '数量', amount: '金额', totalAmount: '总金额', expiredMsg: '此二维码已过期。', autocopied: '✅ 账号已自动复制!' },
  ja: { title: '送金案内', copyBtn: '口座番号コピー', copyDone: '✅ コピー完了!', bankApp: 'アプリで送金', accountHolder: '口座名義', bank: '銀行', accountNumber: '口座番号', memo: 'メモ', quantity: '数量', amount: '金額', totalAmount: '合計', expiredMsg: 'このQRコードは期限切れです。', autocopied: '✅ 口座番号が自動コピーされました!' },
};

export default function DisplayPage() {
  const [searchParams] = useSearchParams();
  const [copied, setCopied] = useState(false);
  const [autoCopied, setAutoCopied] = useState(false);
  const [lang, setLang] = useState('ko');
  const [quantity, setQuantity] = useState(1);
  const [isExpired, setIsExpired] = useState(false);

  const bank = searchParams.get('bank') || '';
  const accountNumber = searchParams.get('accountNumber') || '';
  const accountHolder = searchParams.get('accountHolder') || '';
  const template = searchParams.get('template') || 'web-payment';
  const unitPrice = searchParams.get('unitPrice') || '';
  const expiry = searchParams.get('expiry') || '';
  const bgImage = searchParams.get('bg') || '';
  const nickname = searchParams.get('nickname') || '';

  const t = i18n[lang] || i18n['ko'];
  const fullAccountNumber = `${bank} ${accountNumber}`;
  const totalAmount = unitPrice ? Number(unitPrice) * quantity : 0;

  useEffect(() => {
    if (expiry) {
      const expiryDate = new Date(expiry);
      if (new Date() > expiryDate) setIsExpired(true);
    }
  }, [expiry]);

  useEffect(() => {
    const browserLang = navigator.language.toLowerCase();
    if (browserLang.startsWith('zh')) setLang('zh');
    else if (browserLang.startsWith('ja')) setLang('ja');
    else if (browserLang.startsWith('en')) setLang('en');
    else setLang('ko');
  }, []);

  useEffect(() => {
    if (!accountNumber || isExpired) return;
    const copyText = unitPrice
      ? `${fullAccountNumber} (${t.totalAmount}: ${totalAmount.toLocaleString()}원)`
      : fullAccountNumber;
    navigator.clipboard.writeText(copyText).then(() => {
      setAutoCopied(true);
      setTimeout(() => setAutoCopied(false), 4000);
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountNumber, isExpired]);

  const handleCopy = () => {
    const copyText = unitPrice
      ? `${fullAccountNumber} (${t.totalAmount}: ${totalAmount.toLocaleString()}원)`
      : fullAccountNumber;
    navigator.clipboard.writeText(copyText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleOpenBankApp = (app: typeof bankApps[0]) => {
    const isAndroid = /android/i.test(navigator.userAgent);
    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
    if (isAndroid) {
      const intentUrl = `intent://#Intent;scheme=${app.scheme.replace('://', '')};package=${app.androidPackage};S.browser_fallback_url=https://play.google.com/store/apps/details?id=${app.androidPackage};end`;
      window.location.href = intentUrl;
    } else if (isIOS) {
      const now = Date.now();
      window.location.href = app.scheme;
      setTimeout(() => {
        if (Date.now() - now < 2000) {
          window.location.href = `https://apps.apple.com/kr/app/id${app.iosId}`;
        }
      }, 1000);
    } else {
      window.location.href = app.scheme;
    }
  };

  if (isExpired) {
    return (
      <div className="display-expired">
        <div className="expired-icon">⏰</div>
        <h2>{t.expiredMsg}</h2>
      </div>
    );
  }

  // 배경 이미지 + 상호명 오버레이
  const renderHero = () => (
    <div className="hero-banner">
      <img src={bgImage} alt="업종 배경" className="hero-img" />
      <div className="hero-overlay">
        <span className="hero-nickname">{nickname}</span>
      </div>
    </div>
  );

  const renderContent = () => (
    <>
      {autoCopied && <div className="auto-copy-banner">{t.autocopied}</div>}

      <div className="lang-selector">
        {['ko', 'en', 'zh', 'ja'].map(l => (
          <button key={l} className={`lang-btn ${lang === l ? 'active' : ''}`} onClick={() => setLang(l)}>
            {l === 'ko' ? '한국어' : l === 'en' ? 'EN' : l === 'zh' ? '中文' : '日本語'}
          </button>
        ))}
      </div>

      <div className="account-section">
        <div className="account-row">
          <span className="account-label">{t.accountHolder}</span>
          <span className="account-value">{accountHolder}</span>
        </div>
        <div className="account-row">
          <span className="account-label">{t.bank}</span>
          <span className="account-value">{bank}</span>
        </div>
        <div className="account-number-box">
          <span className="account-number-label">{t.accountNumber}</span>
          <span className="account-number-value">{accountNumber}</span>
        </div>
      </div>

      {unitPrice && (
        <div className="calculator-section">
          <div className="calc-row">
            <span>{t.amount}</span>
            <span>{Number(unitPrice).toLocaleString()}원</span>
          </div>
          <div className="calc-row">
            <span>{t.quantity}</span>
            <div className="quantity-control">
              <button onClick={() => setQuantity(Math.max(1, quantity - 1))}>－</button>
              <span>{quantity}</span>
              <button onClick={() => setQuantity(quantity + 1)}>＋</button>
            </div>
          </div>
          <div className="calc-total">
            <span>{t.totalAmount}</span>
            <span className="total-price">{totalAmount.toLocaleString()}원</span>
          </div>
        </div>
      )}

      <button className="copy-btn" onClick={handleCopy}>
        {copied ? t.copyDone : t.copyBtn}
      </button>

      <p className="copy-notice">계좌번호 복사 후 앱을 이용하세요</p>

      <div className="bank-apps-section">
        <p className="bank-apps-title">{t.bankApp}</p>
        <div className="bank-apps-grid">
          {bankApps.map(app => (
            <div key={app.name} className="bank-app-item" onClick={() => handleOpenBankApp(app)}>
              <div className="bank-app-icon" style={{ backgroundColor: app.bgColor, border: `1px solid ${app.bgColor === '#ffffff' ? '#e9ecef' : app.bgColor}` }}>
                {app.logo ? (
                  <img src={app.logo} alt={app.name} />
                ) : (
                  <span style={{ fontSize: app.initial.length > 2 ? '0.55rem' : '0.75rem', fontWeight: '800', color: app.textColor, textAlign: 'center', lineHeight: '1.2', wordBreak: 'keep-all', letterSpacing: '-0.5px' }}>
                    {app.initial}
                  </span>
                )}
              </div>
              <span>{app.name}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );

  if (template === 'web-payment') {
    return (
      <div className="display-container">
        <div className="display-modern">
          {renderHero()}
          <div className="modern-body">{renderContent()}</div>
        </div>
      </div>
    );
  }

  if (template === 'web-payment-simple') {
    return (
      <div className="display-container">
        <div className="display-simple">
          {renderHero()}
          <div className="simple-body">{renderContent()}</div>
        </div>
      </div>
    );
  }

  if (template === 'web-payment-dark') {
    return (
      <div className="display-container dark-bg">
        <div className="display-dark">
          {renderHero()}
          <div className="dark-body">{renderContent()}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="display-container">
      <div className="display-modern">
        {renderHero()}
        <div className="modern-body">{renderContent()}</div>
      </div>
    </div>
  );
}
